// SIMPLE LODASH INSPIRED FUNCTIONS WITHOUT THE WEIGHT.

/**
 * Finds the value of a specified key in an object or nested object, or returns a default value if the key is not found.
 *
 * @version 1.0.0
 * @since 2023-03-08
 * @param {Object} obj The object to search.
 * @param {(string|string[])} keyToFind The key or array of keys to search for.
 * @param {*} defaultValue The default value to return if the key is not found.
 * @returns {*} The value of the first matching key, or the default value if no keys match.
 * @example
 *
 */

export function findKey(obj, keyToFind, defaultValue) {
    if (!keyToFind || obj === null || obj === undefined) return undefined

    // Handle multiple keys to find
    const keysToFind = Array.isArray(keyToFind) ? keyToFind : [keyToFind]

    function recursiveSearch(currentObj) {
        if (currentObj === null || currentObj === undefined) return undefined

        // If current object is an array, iterate over its elements
        if (Array.isArray(currentObj)) {
            for (let i = 0; i < currentObj.length; i++) {
                const result = recursiveSearch(currentObj[i])
                if (result !== undefined) {
                    return result
                }
            }
        } else if (typeof currentObj === 'object') {
            // Check if current object has any of the keys we're looking for
            for (let key of keysToFind) {
                if (currentObj.hasOwnProperty(key)) {
                    return currentObj[key]
                }
            }
            // Recursively search in each property of the current object
            for (let key in currentObj) {
                if (currentObj.hasOwnProperty(key)) {
                    const result = recursiveSearch(currentObj[key])
                    if (result !== undefined) {
                        return result
                    }
                }
            }
        }
        return undefined
    }

    const result = recursiveSearch(obj)

    return result !== undefined ? result : defaultValue
}

export function hasKeyValue(obj, key, value) {
    try {
        // Base case: if obj is not an object or array, return false
        if (obj === null || typeof obj !== 'object') {
            return false
        }

        // If obj is an array, iterate over elements
        if (Array.isArray(obj)) {
            for (let element of obj) {
                if (hasKeyValue(element, key, value)) {
                    return true
                }
            }
        } else {
            // Check if current object has the key-value pair
            if (obj.hasOwnProperty(key) && obj[key] === value) {
                return true
            }
            // Recurse over object properties
            for (let prop in obj) {
                if (obj.hasOwnProperty(prop)) {
                    if (hasKeyValue(obj[prop], key, value)) {
                        return true
                    }
                }
            }
        }

        return false
    } catch (err) {
        console.error('Cant find key/value', err)
        return false
    }
}

/**
 * Creates an index object from an array of objects using a specified key.
 *
 * @version 1.1.0
 * @since 2023-03-08
 * @param {Array<Object>} arr The array of objects to be indexed.
 * @param {string} key The key to use as the index.
 * @returns {Object} An index object with the specified key as the property key and the entire object as the property value.
 * @description Added support for arrays of keys to be used as index
 */

export function createIndex(arr, key) {
    const index = {}
    arr.reduce((acc, item) => {
        const keyValue = item[key]
        acc[keyValue] = item
        return acc
    }, index)

    return index
}

/**
 * Deeply clones an object or array.
 *
 * @version 1.1.0
 * @since 2023-03-08
 * @param {*} obj - The object or array to clone.
 * @returns {*} A deep copy of the input object or array.
 */
export function deepClone(obj) {
    let clone
    if (obj === null || typeof obj !== 'object') return obj
    if (Array.isArray(obj)) {
        clone = []
        for (let i = 0; i < obj.length; i++) {
            clone[i] = deepClone(obj[i])
        }
        return clone
    }
    if (typeof obj === 'object') {
        clone = {}
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                clone[key] = deepClone(obj[key])
            }
        }
        return clone
    }
}

// updated to support arrays of paths
export const getNestedValue = (obj, path) => {
    const one = (o, p) =>
        String(p)
            .split('.')
            .reduce((acc, part) => acc?.[isNaN(part) ? part : Number(part)], o)
    return Array.isArray(path) ? path.reduce((v, p) => v ?? one(obj, p), undefined) : one(obj, path)
}

export const setNestedValue = (obj, path, value) => {
    const parts = path.split('.')
    const last = parts.pop()
    if (!last) return obj

    const clone = Array.isArray(obj) ? [...obj] : { ...obj }

    let cursor = clone
    for (let i = 0; i < parts.length; i++) {
        const key = isNaN(parts[i]) ? parts[i] : Number(parts[i])
        const nextKey = parts[i + 1]
        const shouldBeArray = nextKey !== undefined && !isNaN(nextKey)

        // build missing node or deep clone existing
        if (cursor[key] === undefined) {
            cursor[key] = shouldBeArray ? [] : {}
        } else {
            cursor[key] = Array.isArray(cursor[key]) ? [...cursor[key]] : { ...cursor[key] }
        }

        cursor = cursor[key]
    }

    cursor[isNaN(last) ? last : Number(last)] = value
    return clone
}

export const deleteNestedValue = (obj, path, { clean = true } = {}) => {
    const parts = path.split('.')
    const last = parts.pop()
    if (!last) return obj

    const clone = Array.isArray(obj) ? [...obj] : { ...obj }

    const stack = [clone]
    let cursor = clone

    // traverse + clone path
    for (let i = 0; i < parts.length; i++) {
        const key = isNaN(parts[i]) ? parts[i] : Number(parts[i])
        if (cursor[key] === undefined) return obj // nothing to delete

        cursor[key] = Array.isArray(cursor[key]) ? [...cursor[key]] : { ...cursor[key] }
        cursor = cursor[key]
        stack.push(cursor)
    }

    // delete target
    const key = isNaN(last) ? last : Number(last)
    if (Array.isArray(cursor)) {
        if (cursor[key] !== undefined) cursor.splice(key, 1)
    } else {
        delete cursor[key]
    }

    // auto-clean: prune empty objects/arrays bottom-up
    if (clean) {
        for (let i = parts.length; i >= 0; i--) {
            const parent = stack[i]
            const k = i === parts.length ? key : isNaN(parts[i]) ? parts[i] : Number(parts[i])
            if (Array.isArray(parent[k]) && parent[k].length === 0) {
                if (Array.isArray(parent)) parent.splice(k, 1)
                else delete parent[k]
            } else if (typeof parent[k] === 'object' && parent[k] && Object.keys(parent[k]).length === 0) {
                if (Array.isArray(parent)) parent.splice(k, 1)
                else delete parent[k]
            }
        }
    }

    return clone
}
// multi-field version
export const setNestedValues = (obj, updates) => {
    return Object.entries(updates).reduce((acc, [path, val]) => setNestedValue(acc, path, val), obj)
}
